import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Alert,
  Divider,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Collapse,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  Send as SendIcon,
  Save as SaveIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  LightbulbOutlined as LightbulbIcon,
} from '@mui/icons-material';
import { teamApi, adminApi, sampleFilesApi } from '@/services/api';
import { getErrorMessage } from '@/utils/errors';
import { useAuth } from '@/context/AuthContext';

const steps = [
  'Essentials',
  'Knowledge Base',
  'Resources',
  'Submit',
];

// ─── Inline tip box ─────────────────────────────────────────────────────────

interface TipBoxProps {
  children: React.ReactNode;
}

function TipBox({ children }: TipBoxProps) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mb: 2 }}>
      <Button
        size="small"
        startIcon={<LightbulbIcon />}
        onClick={() => setOpen((v) => !v)}
        sx={{ color: 'primary.main', textTransform: 'none', fontWeight: 500 }}
      >
        Why this matters
      </Button>
      <Collapse in={open}>
        <Box
          sx={{
            borderLeft: '3px solid',
            borderColor: 'primary.main',
            background: 'action.hover',
            borderRadius: '0 8px 8px 0',
            p: 2,
            mt: 1,
            fontSize: '0.875rem',
            color: 'text.secondary',
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Inline add form wrapper ─────────────────────────────────────────────────

interface InlineAddFormProps {
  label: string;
  open: boolean;
  onToggle: () => void;
  onAdd: () => void;
  isPending?: boolean;
  children: React.ReactNode;
}

function InlineAddForm({ label, open, onToggle, onAdd, isPending, children }: InlineAddFormProps) {
  return (
    <Box sx={{ mb: 2 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={onToggle}
        sx={{ mb: 1 }}
      >
        {label}
      </Button>
      <Collapse in={open}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {children}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={onAdd}
              disabled={isPending}
              startIcon={isPending ? <CircularProgress size={16} /> : undefined}
            >
              {isPending ? 'Adding...' : 'Add'}
            </Button>
            <Button size="small" onClick={onToggle}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── JSON import fallback ────────────────────────────────────────────────────

interface JsonImportLinkProps {
  label: string;
  onDownload: () => void;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

function JsonImportLink({ label, onDownload, onUpload }: JsonImportLinkProps) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ mt: 1 }}>
      <Button
        size="small"
        startIcon={<UploadIcon fontSize="small" />}
        onClick={() => setOpen((v) => !v)}
        sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.8rem' }}
      >
        Import from JSON file instead
      </Button>
      <Collapse in={open}>
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={onDownload}
          >
            Download sample
          </Button>
          <Button
            size="small"
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
          >
            {label}
            <input type="file" hidden accept=".json,.csv" onChange={onUpload} />
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Quick-Start Dialog ──────────────────────────────────────────────────────

interface QuickStartDialogProps {
  open: boolean;
  teamInfo: {
    name: string;
    description: string;
    google_space_id: string;
    jira_project_key: string;
    timezone: string;
  };
  onChange: (field: string, value: string) => void;
  onSave: () => void;
  onSkip: () => void;
  isSaving: boolean;
}

function QuickStartDialog({
  open,
  teamInfo,
  onChange,
  onSave,
  onSkip,
  isSaving,
}: QuickStartDialogProps) {
  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <LinearProgress variant="determinate" value={60} sx={{ height: 6 }} />
      <DialogTitle sx={{ pt: 2.5, pb: 0.5 }}>
        <Typography variant="h6" component="div" fontWeight={600}>
          Let's get your team set up
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Fill in the essentials — takes about 2 minutes
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
          <TextField
            label="Team Name"
            value={teamInfo.name}
            onChange={(e) => onChange('name', e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Google Chat Space ID"
            value={teamInfo.google_space_id}
            onChange={(e) => onChange('google_space_id', e.target.value)}
            required
            fullWidth
            helperText="Open your Chat space → Settings → Space details → copy the ID"
          />
          <TextField
            label="Timezone"
            value={teamInfo.timezone}
            onChange={(e) => onChange('timezone', e.target.value)}
            fullWidth
            helperText="e.g., UTC, Europe/Berlin, America/New_York"
          />
          <Divider>
            <Typography variant="caption" color="text.secondary">
              Optional — add now or later
            </Typography>
          </Divider>
          <TextField
            label="Jira Project Key"
            value={teamInfo.jira_project_key}
            onChange={(e) => onChange('jira_project_key', e.target.value)}
            fullWidth
            helperText="e.g., PROJ, TEAM"
          />
          <TextField
            label="GitHub PAT"
            fullWidth
            placeholder="ghp_..."
            helperText="ghp_... — unlocks issues view, PR sharing, spec-driven dev"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onSkip} sx={{ mr: 'auto' }}>
          Skip — I'll configure later
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={isSaving || !teamInfo.name || !teamInfo.google_space_id}
          endIcon={isSaving ? <CircularProgress size={16} /> : undefined}
        >
          {isSaving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function TeamOnboardingPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [submitNotes, setSubmitNotes] = useState('');

  // Quick-start dialog state — shown once per team per browser session
  const [showQuickStart, setShowQuickStart] = useState(() => {
    return !localStorage.getItem(`wingman_qs_${teamId}`);
  });

  // Team info editing state
  const [teamInfo, setTeamInfo] = useState({
    name: '',
    description: '',
    google_space_id: '',
    jira_project_key: '',
    timezone: 'UTC',
  });

  // Inline add state — Members
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ email: '', role: 'member' as 'admin' | 'team_lead' | 'member' });
  const [addMemberPending, setAddMemberPending] = useState(false);

  // Inline add state — Experts
  const [showAddExpert, setShowAddExpert] = useState(false);
  const [newExpert, setNewExpert] = useState({ name: '', keywords: '' });
  const [addExpertPending, setAddExpertPending] = useState(false);

  // Inline add state — Documentation
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [newDoc, setNewDoc] = useState({ url: '', description: '' });
  const [addDocPending, setAddDocPending] = useState(false);

  // Inline add state — Repositories
  const [showAddRepo, setShowAddRepo] = useState(false);
  const [newRepo, setNewRepo] = useState({ url: '', description: '' });
  const [addRepoPending, setAddRepoPending] = useState(false);

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: team } = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamApi.get(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: onboardingStatus } = useQuery({
    queryKey: ['onboarding-status', teamId],
    queryFn: () => adminApi.getTeamStatus(teamId!).then((res) => res.data).catch(() => null),
    enabled: !!teamId,
  });

  const { data: members } = useQuery({
    queryKey: ['team-members', teamId],
    queryFn: () => teamApi.getMembers(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: documentation } = useQuery({
    queryKey: ['team-documentation', teamId],
    queryFn: () => teamApi.getDocumentation(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: repositories } = useQuery({
    queryKey: ['team-repositories', teamId],
    queryFn: () => teamApi.getRepositories(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: experts } = useQuery({
    queryKey: ['team-experts', teamId],
    queryFn: () => teamApi.getExperts(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  const { data: responsibilities } = useQuery({
    queryKey: ['team-responsibilities', teamId],
    queryFn: () => teamApi.getResponsibilities(teamId!).then((res) => res.data),
    enabled: !!teamId,
  });

  // Keep AI config query so its data is available (used in Team Settings)
  useQuery({
    queryKey: ['team-ai-config', teamId],
    queryFn: () => teamApi.getAIConfig(teamId!).then((res) => res.data).catch(() => null),
    enabled: !!teamId,
  });

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (team) {
      setTeamInfo({
        name: team.name || '',
        description: team.description || '',
        google_space_id: team.google_space_id || '',
        jira_project_key: team.jira_project_key || '',
        timezone: team.timezone || 'UTC',
      });
    }
  }, [team]);

  // ── Mutations ──────────────────────────────────────────────────────────────

  const updateTeamMutation = useMutation({
    mutationFn: (data: Partial<typeof teamInfo>) => teamApi.update(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });

  const submitMutation = useMutation({
    mutationFn: () =>
      adminApi.createRequest({
        team_id: teamId!,
        requested_by_email: user?.email || '',
        notes: submitNotes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', teamId] });
      queryClient.invalidateQueries({ queryKey: ['team', teamId] });
    },
  });

  const resubmitMutation = useMutation({
    mutationFn: (requestId: string) =>
      adminApi.resubmit(requestId, {
        requested_by_email: user?.email || '',
        notes: submitNotes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', teamId] });
    },
  });

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleTeamInfoChange = (field: string, value: string) => {
    setTeamInfo((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveTeamInfo = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { google_space_id, ...updateData } = teamInfo;
    updateTeamMutation.mutate(updateData);
  };

  const handleSaveQuickStart = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { google_space_id, ...updateData } = teamInfo;
    updateTeamMutation.mutate(updateData, {
      onSuccess: () => {
        localStorage.setItem(`wingman_qs_${teamId}`, '1');
        setShowQuickStart(false);
        setActiveStep(1);
      },
    });
  };

  const handleCloseQuickStart = () => {
    localStorage.setItem(`wingman_qs_${teamId}`, '1');
    setShowQuickStart(false);
  };

  const handleDownloadSample = async (type: string) => {
    try {
      const response = await sampleFilesApi.download(type, 'json');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_sample.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const content = await file.text();
      const data = JSON.parse(content);

      switch (type) {
        case 'members':
          await teamApi.importMembers(teamId!, data.members || data);
          break;
        case 'documentation':
          await teamApi.importDocumentation(teamId!, data.items || data);
          break;
        case 'repositories':
          await teamApi.importRepositories(teamId!, data.items || data);
          break;
        case 'experts':
          await teamApi.importExperts(teamId!, data.experts || data);
          break;
        case 'responsibilities':
          await teamApi.importResponsibilities(teamId!, data.items || data);
          break;
      }

      queryClient.invalidateQueries({ queryKey: [`team-${type}`, teamId] });
      alert(`${type} imported successfully!`);
    } catch (error) {
      console.error('Import failed:', error);
      alert('Import failed. Please check the file format.');
    }
  };

  const handleAddMember = async () => {
    if (!newMember.email) return;
    setAddMemberPending(true);
    try {
      await teamApi.importMembers(teamId!, [newMember]);
      queryClient.invalidateQueries({ queryKey: ['team-members', teamId] });
      setShowAddMember(false);
      setNewMember({ email: '', role: 'member' });
    } finally {
      setAddMemberPending(false);
    }
  };

  const handleAddExpert = async () => {
    if (!newExpert.name) return;
    setAddExpertPending(true);
    try {
      await teamApi.importExperts(teamId!, [
        {
          expert_name: newExpert.name,
          keywords: newExpert.keywords
            .split(',')
            .map((k) => k.trim())
            .filter(Boolean),
        },
      ]);
      queryClient.invalidateQueries({ queryKey: ['team-experts', teamId] });
      setShowAddExpert(false);
      setNewExpert({ name: '', keywords: '' });
    } finally {
      setAddExpertPending(false);
    }
  };

  const handleAddDoc = async () => {
    if (!newDoc.url) return;
    setAddDocPending(true);
    try {
      await teamApi.importDocumentation(teamId!, [newDoc]);
      queryClient.invalidateQueries({ queryKey: ['team-documentation', teamId] });
      setShowAddDoc(false);
      setNewDoc({ url: '', description: '' });
    } finally {
      setAddDocPending(false);
    }
  };

  const handleAddRepo = async () => {
    if (!newRepo.url) return;
    setAddRepoPending(true);
    try {
      await teamApi.importRepositories(teamId!, [newRepo]);
      queryClient.invalidateQueries({ queryKey: ['team-repositories', teamId] });
      setShowAddRepo(false);
      setNewRepo({ url: '', description: '' });
    } finally {
      setAddRepoPending(false);
    }
  };

  const handleSubmit = () => {
    if (onboardingStatus?.status === 'revision_requested') {
      resubmitMutation.mutate(onboardingStatus.id);
    } else {
      submitMutation.mutate();
    }
  };

  // ── Step renderers ─────────────────────────────────────────────────────────

  const renderEssentials = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Team Information
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your team's basic information below.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label="Team Name"
          value={teamInfo.name}
          onChange={(e) => setTeamInfo({ ...teamInfo, name: e.target.value })}
          required
          fullWidth
        />
        <TextField
          label="Description"
          value={teamInfo.description}
          onChange={(e) => setTeamInfo({ ...teamInfo, description: e.target.value })}
          multiline
          rows={3}
          fullWidth
          placeholder="Describe what your team does..."
        />
        <TextField
          label="Google Space ID"
          value={teamInfo.google_space_id}
          onChange={(e) => setTeamInfo({ ...teamInfo, google_space_id: e.target.value })}
          required
          fullWidth
          helperText="The unique identifier for your Google Chat space (e.g., spaces/AAAA...)"
        />
        <TextField
          label="JIRA Project Key"
          value={teamInfo.jira_project_key}
          onChange={(e) => setTeamInfo({ ...teamInfo, jira_project_key: e.target.value })}
          fullWidth
          helperText="Optional: e.g., PROJ, TEAM"
        />
        <TextField
          label="Timezone"
          value={teamInfo.timezone}
          onChange={(e) => setTeamInfo({ ...teamInfo, timezone: e.target.value })}
          fullWidth
          helperText="e.g., UTC, Europe/Berlin, America/New_York"
        />
        <Box sx={{ mt: 1 }}>
          <Button
            variant="contained"
            startIcon={updateTeamMutation.isPending ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSaveTeamInfo}
            disabled={updateTeamMutation.isPending || !teamInfo.name || !teamInfo.google_space_id}
          >
            {updateTeamMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
          {updateTeamMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Team information saved successfully!
            </Alert>
          )}
          {updateTeamMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to save: {getErrorMessage(updateTeamMutation.error)}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );

  const renderKnowledgeBase = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Knowledge Base
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add your team members and subject matter experts so the bot knows who to involve.
      </Typography>

      <TipBox>
        The bot uses members to understand team roles and route notifications. It uses experts to
        tag the right person when a question matches their area — better experts means smarter
        routing.
      </TipBox>

      {/* Members section */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Team Members
        {(members?.length ?? 0) > 0 && (
          <Chip
            icon={<CheckIcon />}
            label={`${members!.length} configured`}
            color="success"
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Typography>

      {members && members.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {members.map((m) => (
            <Chip key={m.id} label={`${m.email} (${m.role})`} size="small" />
          ))}
        </Box>
      )}

      <InlineAddForm
        label="Add Member"
        open={showAddMember}
        onToggle={() => setShowAddMember((v) => !v)}
        onAdd={handleAddMember}
        isPending={addMemberPending}
      >
        <TextField
          label="Email"
          value={newMember.email}
          onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
          size="small"
          fullWidth
          type="email"
          placeholder="user@example.com"
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Role</InputLabel>
          <Select
            value={newMember.role}
            label="Role"
            onChange={(e) =>
              setNewMember({ ...newMember, role: e.target.value as typeof newMember.role })
            }
          >
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="team_lead">Team Lead</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
        </FormControl>
      </InlineAddForm>

      <JsonImportLink
        label="Upload members JSON"
        onDownload={() => handleDownloadSample('members')}
        onUpload={(e) => handleFileUpload(e, 'members')}
      />

      <Divider sx={{ my: 3 }} />

      {/* Experts section */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Subject Matter Experts
        {(experts?.length ?? 0) > 0 && (
          <Chip
            icon={<CheckIcon />}
            label={`${experts!.length} configured`}
            color="success"
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Typography>

      {experts && experts.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {experts.map((ex) => (
            <Chip key={ex.id} label={ex.expert_name} size="small" />
          ))}
        </Box>
      )}

      <InlineAddForm
        label="Add Expert"
        open={showAddExpert}
        onToggle={() => setShowAddExpert((v) => !v)}
        onAdd={handleAddExpert}
        isPending={addExpertPending}
      >
        <TextField
          label="Name or email"
          value={newExpert.name}
          onChange={(e) => setNewExpert({ ...newExpert, name: e.target.value })}
          size="small"
          fullWidth
          placeholder="Jane Smith"
        />
        <TextField
          label="Keywords (comma-separated)"
          value={newExpert.keywords}
          onChange={(e) => setNewExpert({ ...newExpert, keywords: e.target.value })}
          size="small"
          fullWidth
          placeholder="kubernetes, deployments, CI/CD"
          helperText="The bot uses these to match questions to the right expert"
        />
      </InlineAddForm>

      <JsonImportLink
        label="Upload experts JSON"
        onDownload={() => handleDownloadSample('experts')}
        onUpload={(e) => handleFileUpload(e, 'experts')}
      />
    </Box>
  );

  const renderResources = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Resources
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Link your documentation and repositories so the bot can answer questions from real content.
      </Typography>

      <TipBox>
        Repos give the agent code context for PRs and spec-driven tasks. Docs feed the knowledge
        base so the bot answers questions from your actual documentation, not guesses.
      </TipBox>

      {/* Documentation section */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Documentation
        {(documentation?.length ?? 0) > 0 && (
          <Chip
            icon={<CheckIcon />}
            label={`${documentation!.length} links`}
            color="success"
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Typography>

      {documentation && documentation.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {documentation.map((doc) => (
            <Chip
              key={doc.id}
              label={doc.title || doc.url}
              size="small"
              component="a"
              href={doc.url}
              target="_blank"
              clickable
            />
          ))}
        </Box>
      )}

      <InlineAddForm
        label="Add Doc Link"
        open={showAddDoc}
        onToggle={() => setShowAddDoc((v) => !v)}
        onAdd={handleAddDoc}
        isPending={addDocPending}
      >
        <TextField
          label="URL"
          value={newDoc.url}
          onChange={(e) => setNewDoc({ ...newDoc, url: e.target.value })}
          size="small"
          fullWidth
          type="url"
          placeholder="https://confluence.example.com/..."
        />
        <TextField
          label="Description"
          value={newDoc.description}
          onChange={(e) => setNewDoc({ ...newDoc, description: e.target.value })}
          size="small"
          fullWidth
          placeholder="Deployment runbook"
        />
      </InlineAddForm>

      <JsonImportLink
        label="Upload documentation JSON"
        onDownload={() => handleDownloadSample('documentation')}
        onUpload={(e) => handleFileUpload(e, 'documentation')}
      />

      <Divider sx={{ my: 3 }} />

      {/* Repositories section */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Repositories
        {(repositories?.length ?? 0) > 0 && (
          <Chip
            icon={<CheckIcon />}
            label={`${repositories!.length} repos`}
            color="success"
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Typography>

      {repositories && repositories.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
          {repositories.map((repo) => (
            <Chip key={repo.id} label={repo.name || repo.url} size="small" />
          ))}
        </Box>
      )}

      <InlineAddForm
        label="Add Repo"
        open={showAddRepo}
        onToggle={() => setShowAddRepo((v) => !v)}
        onAdd={handleAddRepo}
        isPending={addRepoPending}
      >
        <TextField
          label="Repository URL"
          value={newRepo.url}
          onChange={(e) => setNewRepo({ ...newRepo, url: e.target.value })}
          size="small"
          fullWidth
          type="url"
          placeholder="https://github.com/org/repo"
        />
        <TextField
          label="Description"
          value={newRepo.description}
          onChange={(e) => setNewRepo({ ...newRepo, description: e.target.value })}
          size="small"
          fullWidth
          placeholder="Main service repository"
        />
      </InlineAddForm>

      <JsonImportLink
        label="Upload repositories JSON"
        onDownload={() => handleDownloadSample('repositories')}
        onUpload={(e) => handleFileUpload(e, 'repositories')}
      />

      <Divider sx={{ my: 3 }} />

      {/* Responsibilities section */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        Responsibility Matrix
        {(responsibilities?.length ?? 0) > 0 && (
          <Chip
            icon={<CheckIcon />}
            label={`${responsibilities!.length} entries`}
            color="success"
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
        {(responsibilities?.length ?? 0) === 0 && (
          <Chip
            icon={<WarningIcon />}
            label="Not configured"
            color="warning"
            size="small"
            variant="outlined"
            sx={{ ml: 1 }}
          />
        )}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Define which components your team owns and how to escalate out-of-scope topics to other
        teams.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => handleDownloadSample('responsibilities')}
        >
          Download sample
        </Button>
        <Button variant="outlined" size="small" component="label" startIcon={<UploadIcon />}>
          Upload JSON
          <input
            type="file"
            hidden
            accept=".json,.csv"
            onChange={(e) => handleFileUpload(e, 'responsibilities')}
          />
        </Button>
      </Box>
    </Box>
  );

  const renderSubmit = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Submit for Approval
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Review your configuration and submit for admin approval.
      </Typography>

      {onboardingStatus?.status === 'revision_requested' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Admin feedback:</strong> {onboardingStatus.admin_comments}
          </Typography>
        </Alert>
      )}

      <TextField
        label="Notes for Admin"
        multiline
        rows={3}
        fullWidth
        value={submitNotes}
        onChange={(e) => setSubmitNotes(e.target.value)}
        sx={{ mb: 2 }}
        placeholder="Any additional information for the approving admin..."
      />

      {onboardingStatus?.status === 'pending' && (
        <Alert severity="info">
          Your request is pending approval. You'll be notified once reviewed.
        </Alert>
      )}

      {onboardingStatus?.status === 'approved' && (
        <Alert severity="success">Your team has been approved and is active!</Alert>
      )}

      {onboardingStatus?.status === 'rejected' && (
        <Alert severity="error">
          Your request was rejected. Please contact the admin for more information.
        </Alert>
      )}
    </Box>
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderEssentials();
      case 1:
        return renderKnowledgeBase();
      case 2:
        return renderResources();
      case 3:
        return renderSubmit();
      default:
        return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box>
      <QuickStartDialog
        open={showQuickStart}
        teamInfo={teamInfo}
        onChange={handleTeamInfoChange}
        onSave={handleSaveQuickStart}
        onSkip={handleCloseQuickStart}
        isSaving={updateTeamMutation.isPending}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Team Onboarding</Typography>
        {team && (
          <Chip
            label={team.approval_status}
            color={
              team.approval_status === 'approved'
                ? 'success'
                : team.approval_status === 'pending'
                  ? 'warning'
                  : team.approval_status === 'rejected'
                    ? 'error'
                    : 'info'
            }
          />
        )}
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent>
          {renderStepContent(activeStep)}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button disabled={activeStep === 0} onClick={() => setActiveStep((prev) => prev - 1)}>
              Back
            </Button>
            <Box>
              {activeStep < steps.length - 1 ? (
                <Button variant="contained" onClick={() => setActiveStep((prev) => prev + 1)}>
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SendIcon />}
                  onClick={handleSubmit}
                  disabled={
                    submitMutation.isPending ||
                    resubmitMutation.isPending ||
                    onboardingStatus?.status === 'pending' ||
                    onboardingStatus?.status === 'approved'
                  }
                >
                  {onboardingStatus?.status === 'revision_requested'
                    ? 'Resubmit for Approval'
                    : 'Submit for Approval'}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
